import type { Invite, InviteCheck } from '@proref/types';
import type { CreateInviteInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// List endpoint deliberately omits the token (leaked tokens are a takeover vector).
export type InviteListItem = Omit<Invite, 'token'>;

// Create returns the token exactly once so the admin can build the redemption link.
// Includes full_name so the CreatedPanel can echo the recipient's name in the confirmation.
export type CreatedInvite = Pick<
  Invite,
  'id' | 'full_name' | 'email' | 'role' | 'federation_id' | 'sport_id' | 'token' | 'expires_at' | 'created_at'
>;

// Create / resend both report whether the email actually went out (meta.email_sent), so the
// UI can warn when delivery was skipped instead of letting a silent miss look like a success.
export interface CreateInviteResult {
  invite: CreatedInvite;
  emailSent: boolean;
}

export interface ResendInviteResult {
  email: string;
  expires_at: string;
  emailSent: boolean;
}

export async function listInvites(): Promise<InviteListItem[]> {
  const res = await api.get<Envelope<InviteListItem[]>>('/invites');
  return unwrap(res.data);
}

export async function createInvite(input: CreateInviteInput): Promise<CreateInviteResult> {
  const res = await api.post<Envelope<CreatedInvite>>('/invites', input);
  return { invite: unwrap(res.data), emailSent: res.data.meta?.['email_sent'] === true };
}

// Re-send an existing invite's email. The token is never returned — the server fetches it and
// emails the link directly, so resend is strictly an action, not a token-exposing call.
export async function resendInvite(id: string): Promise<ResendInviteResult> {
  const res = await api.post<Envelope<{ email: string; expires_at: string }>>(
    `/invites/${id}/resend`,
  );
  const data = unwrap(res.data);
  return { ...data, emailSent: res.data.meta?.['email_sent'] === true };
}

// Unauthenticated invite metadata lookup the signup page hits with the URL token, so
// it can greet the user by name BEFORE they pick a password. The endpoint never burns
// the token — submit-signup still does that. 404 / 410 map to "this link no longer works"
// states the page can render distinctly from an authentication failure.
export async function checkInvite(token: string): Promise<InviteCheck> {
  const res = await api.get<Envelope<InviteCheck>>('/invites/check', { params: { token } });
  return unwrap(res.data);
}

// One row's verdict from a bulk invite. Mirrors the API's BulkInviteResult.
export interface BulkInviteRowResult {
  email: string;
  full_name: string;
  status: 'created' | 'failed';
  email_sent?: boolean;
  code?: string;
  message?: string;
}

export interface BulkInviteResult {
  results: BulkInviteRowResult[];
  created: number;
  failed: number;
}

// Invite a roster in one request. Answers 200 even when some rows failed — a partial success is
// the expected outcome — so the caller reads the per-row verdicts, never just the status code.
export async function createInvitesBulk(invites: CreateInviteInput[]): Promise<BulkInviteResult> {
  const res = await api.post<Envelope<BulkInviteRowResult[]>>('/invites/bulk', { invites });
  const results = unwrap(res.data);
  const created = Number(res.data.meta?.['created'] ?? results.filter((r) => r.status === 'created').length);
  return { results, created, failed: results.length - created };
}
